import { Comment } from '@mui/icons-material';
import { type FeaturedThreadRes } from '@on3/api';
import { Avatar } from '@on3/ui-lib/components/Avatar';
import { Link } from '@on3/ui-lib/components/Link';
import { Tag } from '@on3/ui-lib/components/Tag';
import { toTimeDistance } from '@on3/ui-lib/utils/date';
import { formatNumber } from '@on3/ui-lib/utils/formatters';
import { slugify } from '@on3/ui-lib/utils/slugify';
import { createTitle } from '@public/utils/meta';
import { differenceInHours, format } from 'date-fns';

import styles from './MegaBoardModuleItem.module.scss';

export const ModuleItem = ({
  threadKey,
  title,
  tags,
  board,
  postDate,
  viewCount,
  replyCount,
  firstPost,
  lastPost,
}: FeaturedThreadRes) => {
  const isNew = differenceInHours(new Date(), new Date(postDate * 1000)) < 24;
  const filteredTags = tags.filter(
    (tag) => !['featured', 'megaboard'].includes(tag),
  );
  const showBoardAvatar = !!board?.organization?.defaultAsset?.source;

  return (
    <article className={styles.block}>
      <div className={styles.top}>
        <div className={styles.org}>
          {showBoardAvatar && (
            <Avatar
              altText={board.organization?.defaultAsset.altText}
              domain={board.organization?.defaultAsset.domain}
              height={24}
              source={board.organization?.defaultAsset.source}
              width={24}
            />
          )}
          <Link href={board.url} title={board.title}>
            {createTitle(
              board.organization?.name && `${board.organization?.name} -`,
              board.title,
            )}
          </Link>
        </div>
        <div className={styles.tags}>
          {isNew && <Tag type="New" />}
          {filteredTags.map((tag) => (
            <Tag key={tag} type={tag} />
          ))}
        </div>
        <span className={styles.viewCount}>
          {formatNumber(viewCount)} views
        </span>
      </div>
      <header className={styles.header}>
        <div>
          <Avatar height={50} source={firstPost.author.avatarUrl} width={50} />
        </div>
        <div className={styles.meta}>
          <h3>
            <Link href={`/megaboard/${slugify(title)}-${threadKey}`}>
              {title}
            </Link>
          </h3>
          <div className={styles.row}>
            <div className={styles.postMeta}>
              <div>
                by{' '}
                <span className={styles.username}>
                  {firstPost.author.username || ''}
                </span>
              </div>
              <span>{format(postDate * 1000, 'MMM dd, h:mm a')}</span>
              <Link
                className={styles.replyCount}
                href={`/megaboard/${slugify(title)}-${threadKey}`}
              >
                <Comment />
                {replyCount}
              </Link>
            </div>
            <div className={styles.lastPost}>
              <div className={styles.latestActivity}>
                <span>
                  {toTimeDistance(lastPost.postDate * 1000, true, true)}
                </span>
                <span className={styles.username}>
                  {lastPost.author.username}
                </span>
              </div>
              {lastPost.author.avatarUrl && (
                <Avatar
                  altText={lastPost.author.username}
                  height={24}
                  source={lastPost.author.avatarUrl}
                  width={24}
                />
              )}
            </div>
          </div>
        </div>
      </header>
    </article>
  );
};
